import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Table } from 'primeng/table';
import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';
import { UserRolesService } from '@src/app/shared/services/userRoles.service';
import { ItemDisposition, ItemsService, ItemDetailsResponse, WarrantyOperationType, RetailerResponse, RetailersService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { ConfirmationService } from 'primeng/api';
import { SessionService } from "@app/shared/services/session.service";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { Features } from '@src/app/shared/backend/auth/Feature';

@Component({
    selector: 'app-view-item',
    templateUrl: './view-item.component.html',
    styleUrl: './view-item.component.scss',
    host: {
        class: 'app-view-item'
    },
    providers: [FriendlyBooleanPipe, UserRolesService, ConfirmationService],
    standalone: false
})
export class ViewItemComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  companyHasImplicitSales: boolean = false;
  showSecurityCodeField: boolean = false;
  showSecurityCodeVersionField: boolean = false;
  companyIsPrepaired: boolean = false;
  companyHasCpo: boolean = false;
  delete: boolean = false;
  allowModifyItem: boolean = false;
  allowDigitalCard: boolean = false;
  canViewSales: boolean = false;
  canViewWarranties: boolean = false;
  canViewShipments: boolean = false;
  canViewInventories: boolean = false;
  canDeclareTheft: boolean = false;
  canDeclareRecovery: boolean = false;
  canCreateDigitalCard: boolean = false;
  itemId: number = 0;
  itemOperationsData: any;
  itemEventsData: any;
  item: ItemDetailsResponse = {
    id: 0,
    serialNumber: '',
    isGenuine: false,
    isCpo: false,
    product: {
      id: 0,
      name: '',
      sku: '',
      description: '',
      isCustomisable: false,
      isActive: false,
      isLimitedEdition: false,
      isSerialNumberRequired: false,
      warrantyDurationMonths: 0,
      productType: {
        id: 0,
        name: '',
        code: ''
      }
    }
  };
  itemIsStolen: boolean = false;
  itemCanBeModified: boolean = false;

  displayDeclareTheftModalbox: boolean = false;
  displayDeclareRecoveredModalbox: boolean = false;

  // local pagination
  itemOperationsRows: number = 10;
  itemEventsRows: number = 10;
  itemOperationsPageSize: number = 5;
  itemOperationsFirstPage: number = 0;

  store: RetailerResponse = {};
  storesList: any[] = [];
  storesTotalRecords: number = 0;
  storesRowsPerPageOption: number = 10;

  allowItemOperationsSoldByAnotherRetailer: boolean = false;

  @ViewChild('dtOperationsHistoryTable') table: Table | undefined;

  public declareTheftForm: FormGroup = this.fb.group({
    reason: ['', [Validators.maxLength(50)]],
    retailerId: ['', [Validators.required]]
  });

  public declareRecoveredForm: FormGroup = this.fb.group({
    reason: ['', [Validators.required, Validators.maxLength(50)]],
    retailerId: ['', [Validators.required]]
  });

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private session: SessionService,
    private itemsService: ItemsService,
    private retailersService: RetailersService,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {

    this.session.currentCompanyData$.subscribe(company => {
      this.showSecurityCodeField = company?.showSecurityCode ?? false;
      this.showSecurityCodeVersionField = company?.showSecurityCodeVersion ?? false;
      this.companyIsPrepaired = company?.isPrepaired ?? false;
      this.companyHasCpo = company?.hasCpo ?? false;
      this.companyHasImplicitSales = company?.implicitSaleCreation ?? false;

      this.setItemOperationsData();
    });

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(params => {
        this.itemId = Number(params['itemId']);
        if (this.itemId && this.itemId > 0) {
          this.get();
        }
      });

    this.session.currentCompanyData$.subscribe(company => {
      this.showSecurityCodeField = company?.showSecurityCode ?? false;
      this.showSecurityCodeVersionField = company?.showSecurityCodeVersion ?? false;
      this.companyIsPrepaired = !!company?.isPrepaired;
      this.companyHasCpo = !!company?.hasCpo;
      this.allowItemOperationsSoldByAnotherRetailer = company?.allowItemOperationsSoldByAnotherRetailer ?? false;
    });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowModifyItem = await this.permissionService.hasActionPermissions(Actions.modify_items);
    this.allowDigitalCard = await this.permissionService.hasActionPermissions(Actions.digitalcards) && await this.permissionService.hasFeaturePermissions(Features.DigitalCards);
    this.canViewSales = await this.permissionService.hasActionPermissions(Actions.view_sales);
    this.canViewWarranties = await this.permissionService.hasActionPermissions(Actions.view_warranties);
    this.canDeclareTheft = await this.permissionService.hasActionPermissions(Actions.stolen_items);
    this.canDeclareRecovery = await this.permissionService.hasActionPermissions(Actions.recovered_items);
    this.canViewShipments = await this.permissionService.hasActionPermissions(Actions.view_shipments);
    this.canViewInventories = await this.permissionService.hasActionPermissions(Actions.view_inventories);
  }

  override async get() {
    try {
      this.item = await firstValueFrom(this.itemsService.apiItemsDetailsIdGet(this.itemId));

      this.itemEventsData = [];
      this.canCreateDigitalCard = !!this.item?.warranties?.some(w => w?.isActive);

      this.setItemOperationsData();

      this.item?.events?.forEach(ev =>
        this.itemEventsData.push(ev)
      );
      this.itemIsStolen = this.item.disposition === ItemDisposition.Stolen;
      this.itemCanBeModified = this.item.disposition !== ItemDisposition.Sold && this.item.disposition !== ItemDisposition.Stolen;
      this.itemEventsData.sort((a: any, b: any) =>
        new Date(a.eventTime).getTime() - new Date(b.eventTime).getTime());
    } catch (error) {
      console.log('Error fetching item data:', error);
    }
  }

  private setItemOperationsData(): void {
    this.itemOperationsData = [];

    // UX: hide Associate/Disassociate operation for implicit sale companies
    this.item?.warranties?.forEach(warranty => {
      warranty?.warrantyOperations?.forEach(operation => {
        const isImplicitSaleOperation = this.companyHasImplicitSales && (
          operation.warrantyOperationType === WarrantyOperationType.Associate ||
          operation.warrantyOperationType === WarrantyOperationType.Disassociate
        );

        if (isImplicitSaleOperation) {
          return;
        }

        this.itemOperationsData.push({
          warrantyId: warranty.id,
          code: warranty.code,
          operation
        });
      });
    });
  }

  createDigitalCard() {
    this.router.navigate([`/digital-card/${this.itemId}`]);
  }

  goToEditItem() {
    this.router.navigate([`/items/${this.itemId}/edit`]);
  }

  onPageChange(event: any) {
    this.itemOperationsFirstPage = event.first;
  }

  declareTheft() {
    this.onHideDeclareTheftAction();
    this.getStores(null);
    this.displayDeclareTheftModalbox = true;
  }

  onCancelTheftModal() {
    this.displayDeclareTheftModalbox = false;
    this.onHideDeclareTheftAction();
  }

  declareRecover() {
    this.onHideDeclareRecoveredAction();
    this.getStores(null);
    this.displayDeclareRecoveredModalbox = true;
  }

  onCancelRecoverModal() {
    this.displayDeclareRecoveredModalbox = false;
    this.onHideDeclareRecoveredAction();
  }

  onHideDeclareTheftAction() {
    this.declareTheftForm.reset({
      reason: ''
    });

    this.declareTheftForm.markAsPristine();
    this.declareTheftForm.markAsUntouched();
  }

  onHideDeclareRecoveredAction() {
    this.declareRecoveredForm.reset({
      reason: ''
    });

    this.declareRecoveredForm.markAsPristine();
    this.declareRecoveredForm.markAsUntouched();
  }

  onLazyLoadStores(event: any) {
    this.getStores(event);
  }
    
  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  async getStores(event: any) {
      const filters: any[] = [];
      const search = event?.search?.trim() || null;
      const activePage = Math.max(1, event?.pageIndex ?? 1);
  
      const payload : FilterRequestForCombo = {
        pageNumber: activePage,
        pageSize: event?.pageSize ?? this.storesRowsPerPageOption
      }
  
      if (search) {
        payload.textFilter = search;
      }
  
      // only active retailers
      filters.push({
        propertyName: 'isActive',
        operator: 'eq',
        values: ['true']
      });
    
      filters.push({
        propertyName: 'brands.id',
        operator: 'in',
        values: [this.item.product.brandId?.toString()]
      });
  
      if(!this.allowItemOperationsSoldByAnotherRetailer && this.item.saleRetailerId != null)
      {
        filters.push({
              propertyName: 'id',
              operator: 'eq',
              values: [this.item.saleRetailerId.toString()]
            });
      }

      payload.additionalFilters = filters;
  
      const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));
      this.storesList = storesPage.results ?? [];
      this.storesTotalRecords = storesPage.totalCount ?? 0;

      if(this.storesTotalRecords == 1)
      {
          this.declareTheftForm.get('retailerId')?.setValue(
                this.storesList.map((r: any) => r.id),
                { emitEvent: false }
              );
          this.declareRecoveredForm.get('retailerId')?.setValue(
                this.storesList.map((r: any) => r.id),
                { emitEvent: false }
              );
      }   
    }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.itemsService.apiItemsDeleteCheckBatchPost([this.itemId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteItem(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.itemsService.apiItemsDeleteIdDelete(this.itemId));
      this.router.navigate([`/items`]);
    }
  }

  async onSubmitTheftForm() {
    const payload = {
      itemId: this.itemId,
      reason: this.declareTheftForm.controls['reason'].value,
      retailerId: this.declareTheftForm.controls['retailerId'].value.toString()
    };

    await firstValueFrom(this.itemsService.apiItemsStolenPut(payload));

    this.displayDeclareTheftModalbox = false;
    this.onHideDeclareTheftAction();
    await this.get();

    this.router.navigate([`/items/${this.itemId}`]);
  }

  async onSubmitRecoverForm() {
    const payload = {
      itemId: this.itemId,
      reason: this.declareRecoveredForm.controls['reason'].value,
      retailerId: this.declareRecoveredForm.controls['retailerId'].value.toString()
    };

    await firstValueFrom(this.itemsService.apiItemsRecoveredPut(payload));

    this.displayDeclareRecoveredModalbox = false;
    this.onHideDeclareRecoveredAction();
    await this.get();

    this.router.navigate([`/items/${this.itemId}`]);
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
